import { AIRunType } from '../../ai/ai.daily.usage.model';

/**
 * AI usage features — the products AI usage is reported against.
 *
 * These are things a tenant turns on, NOT the agents and workflows that implement them.
 * jms-ai logs usage under a run id: the name of whichever workflow or agent executed. One
 * product can therefore produce several run ids — Bill Import runs the scan-line-item
 * workflow, which internally calls an image grouper and a document extractor.
 *
 * Everything here is shared by the API (which groups stored usage) and the web (which
 * renders the admin run-id detail), so the two can never disagree about what a run id means.
 */

export enum AIUsageFeature {
  WorkBuddyAgent = 'workbuddy-agent',
  BillImport = 'bill-import',
  TaskValidation = 'task-validation',
  TextRefiner = 'text-refiner',
  VoiceToText = 'voice-to-text',
}

export interface AIUsageFeatureMeta {
  id: AIUsageFeature;
  name: string;
  color: string;
  icon: string;
}

/** Display metadata per feature. Names match Settings → AI & Agents. */
export const AI_USAGE_FEATURE_META: Record<AIUsageFeature, AIUsageFeatureMeta> = {
  [AIUsageFeature.WorkBuddyAgent]: { id: AIUsageFeature.WorkBuddyAgent, name: 'WorkBuddy Agent', color: '#4F46E5', icon: 'fa-regular fa-robot' },
  [AIUsageFeature.BillImport]: { id: AIUsageFeature.BillImport, name: 'Bill Import', color: '#0D9488', icon: 'fa-regular fa-file-lines' },
  [AIUsageFeature.TaskValidation]: { id: AIUsageFeature.TaskValidation, name: 'Task Validation', color: '#D97706', icon: 'fa-regular fa-list-check' },
  [AIUsageFeature.TextRefiner]: { id: AIUsageFeature.TextRefiner, name: 'Text Refiner', color: '#DB2777', icon: 'fa-regular fa-wand-magic-sparkles' },
  [AIUsageFeature.VoiceToText]: { id: AIUsageFeature.VoiceToText, name: 'Voice to Text', color: '#0284C7', icon: 'fa-regular fa-microphone' },
};

/** Stable display order for feature rows that carry no usage ordering of their own. */
export const AI_USAGE_FEATURE_ORDER: readonly AIUsageFeature[] = [AIUsageFeature.WorkBuddyAgent, AIUsageFeature.BillImport, AIUsageFeature.TaskValidation, AIUsageFeature.TextRefiner, AIUsageFeature.VoiceToText];

/**
 * Run id (jms-ai workflow or agent id) → the feature it bills to.
 *
 * Workflow ids are the entry points and do most of the work: a workflow run is logged as one
 * entry with its sub-agents rolled into agentBreakdown. The *-agent entries are a fallback for
 * runs logged standalone because their trace lost its workflow span — image-grouper-agent does
 * this every time it runs, so without its entry Bill Import would split across two rows.
 *
 * Deliberately absent: job-overview / job-overview-agent. That feature is deprecated, so it is
 * left unmapped and falls through to a row of its own rather than being presented as a current
 * product. Any unmapped id behaves the same way — usage is never silently dropped or merged.
 */
export const AI_USAGE_RUN_ID_TO_FEATURE: Record<string, AIUsageFeature> = {
  // WorkBuddy Agent — the assistant, the one top-level agent registered in Mastra.
  'workbuddy-agent': AIUsageFeature.WorkBuddyAgent,

  // Bill Import — scan-line-item workflow plus the agents it runs internally.
  'scan-line-item': AIUsageFeature.BillImport,
  'image-grouper-agent': AIUsageFeature.BillImport,
  'document-extractor-agent': AIUsageFeature.BillImport,

  // Task Validation.
  'task-submission-validation': AIUsageFeature.TaskValidation,
  'task-submission-validation-agent': AIUsageFeature.TaskValidation,

  // Text Refiner — refine-my-text and enhance-my-prompt share one toggle and one row.
  'refiner-workflow': AIUsageFeature.TextRefiner,
  'refiner-agent': AIUsageFeature.TextRefiner,
  'prompt-enhancer-workflow': AIUsageFeature.TextRefiner,
  'prompt-enhancer-agent': AIUsageFeature.TextRefiner,
  'enhance-prompt': AIUsageFeature.TextRefiner,

  // Voice to Text — transcription, then the cleanup pass over the transcript.
  'speech-to-text-workflow': AIUsageFeature.VoiceToText,
  'transcript-cleaner-agent': AIUsageFeature.VoiceToText,
};

/**
 * Entry points registered in jms-ai — the one top-level agent plus the workflows.
 *
 * The admin run table shows this whole roster even at zero usage, so "this never ran" is
 * distinguishable from "this is not reported". Sub-agents are deliberately excluded: they
 * appear nested under their parent run, and only surface as their own row when a trace
 * detaches. job-overview is excluded as deprecated, but still appears if it has usage.
 */
export const AI_USAGE_TOP_LEVEL_RUNS: readonly { id: string; runType: AIRunType }[] = [
  { id: 'workbuddy-agent', runType: 'agent_run' },
  { id: 'scan-line-item', runType: 'workflow_run' },
  { id: 'task-submission-validation', runType: 'workflow_run' },
  { id: 'refiner-workflow', runType: 'workflow_run' },
  { id: 'prompt-enhancer-workflow', runType: 'workflow_run' },
  { id: 'speech-to-text-workflow', runType: 'workflow_run' },
];

/**
 * Categorical palette for unmapped run ids (feature rows + stacked chart segments).
 * Hues are spread around the wheel rather than clustered in blue so adjacent stack segments
 * stay distinguishable, and every entry clears 4.5:1 on white for the accompanying text.
 */
export const AI_USAGE_FEATURE_PALETTE: readonly string[] = [
  '#4F46E5', // indigo
  '#0D9488', // teal
  '#D97706', // amber
  '#DB2777', // pink
  '#0284C7', // sky
  '#65A30D', // lime
  '#9333EA', // purple
  '#DC2626', // red
];

/**
 * Collapse the wrapper jms-ai puts around stored keys so equivalent ids compare equal.
 * e.g. "agent run: 'workbuddy-agent'" and "workbuddy-agent" → "workbuddy-agent".
 * agentBreakdown keys carry the same wrapper, so they normalize through here too.
 */
export function normalizeAIUsageRunId(runId: string): string {

  if (!runId) {
    return 'unknown';
  }

  let id = runId.trim().toLowerCase();
  const quotedRun = id.match(/^(?:agent|workflow)\s*run:\s*['"]?(.+?)['"]?\s*$/i);
  if (quotedRun) {
    id = quotedRun[1].trim();
  }

  return id
    .replace(/^agent\s*run[:\s]*/i, '')
    .replace(/^workflow\s*run[:\s]*/i, '')
    .replace(/['"]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Title-case a run id as itself — "scan-line-item" → "Scan Line Item".
 * Use for the admin run-level detail, where the point is to see the actual workflow or agent
 * that ran; use resolveAIUsageFeatureName instead when showing which product it bills to.
 */
export function humanizeAIUsageRunId(runId: string): string {

  if (!runId) {
    return 'Unknown';
  }

  return normalizeAIUsageRunId(runId)
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

/** Run id → feature id. Unmapped ids keep their own id so their spend stays visible. */
export function resolveAIUsageFeatureId(runId: string): string {

  const normalized = normalizeAIUsageRunId(runId);
  return AI_USAGE_RUN_ID_TO_FEATURE[normalized] ?? normalized;
}

/** Display name for a run or feature id, title-casing anything not in the catalog. */
export function resolveAIUsageFeatureName(id: string): string {

  if (!id) {
    return 'Unknown';
  }

  return AI_USAGE_FEATURE_META[resolveAIUsageFeatureId(id) as AIUsageFeature]?.name
    ?? humanizeAIUsageRunId(id);
}

/**
 * Swatch colour for a run or feature id. Catalog features take their fixed colour so it never
 * shifts between periods; only unmapped ids fall back to a position-based palette entry.
 */
export function resolveAIUsageFeatureColor(id: string, index = 0): string {

  return AI_USAGE_FEATURE_META[resolveAIUsageFeatureId(id) as AIUsageFeature]?.color
    ?? AI_USAGE_FEATURE_PALETTE[index % AI_USAGE_FEATURE_PALETTE.length];
}
